import {
  EJobRecordStatus,
  ELightGalleryType,
  type IJobRecord,
  type IJobRecordChoiceSummary,
  type IJobRecordMetricsSummary,
  type IJobRecordQuestion,
  type IQuestion,
  IsJobRecordChapter,
  IsJobRecordMockTest,
  IsJobRecordQuestion,
  IsJobRecordStation,
} from '@quesmed/types/models';
import { CiMemoPad } from 'react-icons/ci';
import { FaBoxOpen, FaCheck, FaHourglassHalf, FaRocket, FaUnlock } from 'react-icons/fa6';
import { FiCheckSquare } from 'react-icons/fi';
import { GrTest } from 'react-icons/gr';
import type { IconType } from 'react-icons/lib';
import { MdOutlinePublishedWithChanges } from 'react-icons/md';
import { RxCross2 } from 'react-icons/rx';
import type { IAssetSchema } from './asset';

export type RecordActionConfig = {
  title: string;
  description: string;
  color: 'success' | 'danger' | 'secondary' | 'warning' | 'default';
  icon: React.ComponentType<{ className?: string }>;
  type: 'warn' | 'danger' | 'success' | 'info' | 'default';
  status: EJobRecordStatus;
};

export const getJobRecordTitle = (record: IJobRecord['data']) => {
  // if ('stem' in record && record.stem) {
  //   return record.stem;
  // }
  if (IsJobRecordQuestion(record) || IsJobRecordMockTest(record)) {
    return record?.question;
  }
  if (IsJobRecordStation(record)) {
    return record?.name;
  }
  if (IsJobRecordChapter(record)) {
    return record?.explanation;
  }

  return 'Not found';
};

export const recordStatusColorMap: Record<
  EJobRecordStatus,
  { className: string; icon: { type: IconType; className: string } }
> = {
  [EJobRecordStatus.ALL]: {
    className: 'bg-default-100 text-default-500 border border-default-200',
    icon: { type: CiMemoPad, className: 'size-3.5 text-default-500' },
  },
  [EJobRecordStatus.DRAFT]: {
    className: 'bg-primary-200 text-primary-800 border border-primary-400',
    icon: { type: FaHourglassHalf, className: 'size-3.5 text-primary-800' },
  },
  // [EJobRecordStatus.READY]: {
  //   className: 'bg-warning-100/60 text-warning-500',
  //   icon: { type: FaFireAlt, className: 'size-3.5 text-warning-500' },
  // },
  [EJobRecordStatus.READY]: {
    className: 'bg-secondary-50 text-secondary-600 border border-secondary-200',
    icon: { type: FaBoxOpen, className: 'size-3.5 text-secondary-600' },
  },
  [EJobRecordStatus.REJECTED]: {
    className: 'bg-danger-50 text-danger-500 border border-danger-200',
    icon: { type: RxCross2, className: 'size-3.5 text-danger-500' },
  },
  [EJobRecordStatus.APPROVED]: {
    className: 'bg-success-50 text-success-500 border border-success-200',
    icon: { type: FaCheck, className: 'size-3.5 text-success-500' },
  },
  [EJobRecordStatus.TESTING]: {
    className: 'bg-pink-100 dark:bg-pink-950 text-pink-500 border border-pink-200 dark:border-pink-800',
    icon: { type: GrTest, className: 'size-3.5 text-pink-500' },
  },
  // [EJobRecordStatus.PARTIAL_TRIALING]: {
  //   className: 'bg-lime-100 dark:bg-lime-950 text-lime-500',
  //   icon: { type: FaStarHalfAlt, className: 'size-3.5 text-lime-500' },
  // },
  [EJobRecordStatus.PUBLISHED]: {
    className: 'bg-cyan-100 dark:bg-cyan-950 text-cyan-500 border border-cyan-200 dark:border-cyan-800',
    icon: { type: MdOutlinePublishedWithChanges, className: 'size-3.5 text-cyan-500' },
  },
  [EJobRecordStatus.PAID]: {
    className:
      'bg-emerald-100 dark:bg-emerald-950 text-emerald-600 border border-emerald-200 dark:border-emerald-800',
    icon: { type: MdOutlinePublishedWithChanges, className: 'size-3.5 text-emerald-600' },
  },
  // [EJobStatus.CLOSED]: {
  //   className: 'bg-rose-50 dark:bg-rose-950 text-rose-500',
  //   icon: { type: FaLock, className: 'size-3.5 text-rose-500' },
  // },
};

export const RECORD_ACTION_CONFIGS: Partial<Record<EJobRecordStatus, RecordActionConfig>> = {
  [EJobRecordStatus.DRAFT]: {
    title: 'Unlock',
    description: 'Are you sure you want to unlock these records?',
    color: 'warning',
    icon: FaUnlock,
    status: EJobRecordStatus.DRAFT,
    type: 'warn',
  },
  [EJobRecordStatus.READY]: {
    title: 'Mark Ready',
    description: 'Are you sure you want to mark these records as ready?',
    color: 'secondary',
    icon: FaBoxOpen,
    status: EJobRecordStatus.READY,
    type: 'warn',
  },
  [EJobRecordStatus.REJECTED]: {
    title: 'Reject',
    description: 'Are you sure you want to reject these records?',
    color: 'danger',
    icon: RxCross2,
    status: EJobRecordStatus.REJECTED,
    type: 'danger',
  },
  [EJobRecordStatus.APPROVED]: {
    title: 'Approve',
    description: 'Are you sure you want to approve these records?',
    color: 'success',
    icon: FiCheckSquare,
    status: EJobRecordStatus.APPROVED,
    type: 'success',
  },
  [EJobRecordStatus.TESTING]: {
    title: 'Test',
    description: 'Are you sure you want to test these records?',
    color: 'default',
    icon: GrTest,
    status: EJobRecordStatus.TESTING,
    type: 'default',
  },
  [EJobRecordStatus.PUBLISHED]: {
    title: 'Publish',
    description: 'Are you sure you want to publish these records?',
    color: 'secondary',
    icon: FaRocket,
    status: EJobRecordStatus.PUBLISHED,
    type: 'info',
  },
  [EJobRecordStatus.PAID]: {
    title: 'Mark as Paid',
    description: 'Are you sure you want to mark these records as paid?',
    color: 'success',
    icon: FiCheckSquare,
    status: EJobRecordStatus.PAID,
    type: 'success',
  },
};

export const sjtChoiceTemplates: Record<
  string,
  {
    question: string;
    choices: Array<IJobRecordQuestion['choices'][0] & { options?: string[] }>;
    stem: string;
  }
> = {
  Importance: {
    question:
      'How **important** to take into account are the following considerations for **<u>NAME</u>** when deciding how to respond to the situation?',
    choices: [
      { name: 'Very important', explanation: '', answer: true, label: 'a' },
      { name: 'Important', explanation: '', answer: false, label: 'b' },
      { name: 'Of minor importance', explanation: '', answer: false, label: 'c' },
      { name: 'Not important at all', explanation: '', answer: false, label: 'd' },
    ],
    stem: 'How **important** to take into account are the following considerations for **<u>NAME</u>** when deciding how to respond to the situation?',
  },
  Appropriateness: {
    question: 'How **appropriate** are each of the following responses by **<u>NAME</u>** in this situation?',
    choices: [
      {
        name: 'A very appropriate thing to say',
        explanation: '',
        answer: true,
        label: 'a',
        options: ['A very appropriate thing to say', 'A very appropriate thing to do'],
      },
      {
        name: 'Appropriate, but not ideal',
        explanation: '',
        answer: false,
        label: 'b',
      },
      { name: 'Inappropriate, but not awful', explanation: '', answer: false, label: 'c' },
      {
        name: 'A very inappropriate thing to say',
        explanation: '',
        answer: false,
        label: 'd',
        options: ['A very inappropriate thing to say', 'A very inappropriate thing to do'],
      },
    ],
    stem: 'How **appropriate** are each of the following responses by **<u>NAME</u>** in this situation?',
  },
  MostLeast: {
    question: `Choose **both** the **one most appropriate** action **and** the **one least appropriate** action that **<u>NAME</u>** should take in response to this situation.
You will not receive any marks for this question unless you select **both** the most and least appropriate actions.
    `,
    choices: [
      { name: 'A very appropriate thing to say', explanation: '', answer: true, label: 'a' },
      { name: 'Appropriate, but not ideal', explanation: '', answer: false, label: 'b' },
      { name: 'Inappropriate, but not awful', explanation: '', answer: false, label: 'c' },
      { name: 'A very inappropriate thing to say', explanation: '', answer: false, label: 'd' },
    ],
    stem: `Choose **both** the **one most appropriate** action **and** the **one least appropriate** action that **<u>NAME</u>** should take in response to this situation.
You will not receive any marks for this question unless you select **both** the most and least appropriate actions.`,
  },
};

export const getAssetMapping = (assets?: Pick<IAssetSchema, 'type' | 'choiceLabel'>[] | null) => {
  if (!assets?.length) return {};
  // Build assetMapping for the current record
  const assetMapping = assets.reduce(
    (acc, asset) => {
      switch (asset.type) {
        case ELightGalleryType.QUESTION:
        case ELightGalleryType.EXAMINER:
        case ELightGalleryType.CANDIDATE:
        case ELightGalleryType.WALKTHROUGH:
        case ELightGalleryType.ACTOR:
        case ELightGalleryType.EXPLANATION:
        case ELightGalleryType.LEARNING_POINT:
        case ELightGalleryType.STEM: {
          const total = assets.filter((a) => a.type === asset.type).length;
          acc[`${asset.type}`] = total;
          break;
        }
        case ELightGalleryType.CHOICE_EXPLANATION:
        case ELightGalleryType.CHOICE: {
          const total = assets.filter(
            (a) => a.type === asset.type && a.choiceLabel === asset.choiceLabel,
          ).length;
          acc[`${asset.type}-${asset.choiceLabel}`] = total;
          break;
        }
        default:
          break;
      }
      return acc;
    },
    {} as Record<string, number>,
  );

  return assetMapping;
};

export const getMetricsSummary = (question?: IQuestion) => {
  if (!question)
    return {
      choiceSummary: [],
      metricsSummary: {
        id: '',
        likes: 0,
        dislikes: 0,
        correct: 0,
        incorrect: 0,
        total: 0,
        public: false,
        enabled: false,
        commentCount: 0,
        comments: [],
        percentage: 0,
      },
    };
  const { correct, incorrect, total } = question?.choices?.reduce(
    (acc, choice) => {
      acc.total += choice.votes;
      if (choice.answer) {
        acc.correct += choice.votes;
      } else {
        acc.incorrect += choice.votes;
      }
      return acc;
    },
    { correct: 0, incorrect: 0, total: 0 },
  ) ?? { correct: 0, incorrect: 0, total: 0 };
  const choiceSummary: IJobRecordChoiceSummary[] = question?.choices?.map((choice) => ({
    id: choice.id,
    label: choice.label,
    votes: choice.votes,
    percentage: total ? Number(((choice.votes / total) * 100).toFixed(2)) : 0,
    questionId: question.id,
  }));
  const metricsSummary: IJobRecordMetricsSummary & { enabled: number } = {
    id: question.id.toString(),
    likes: question?.likes || 0,
    dislikes: question?.dislikes || 0,
    total,
    correct,
    incorrect,
    public: false,
    enabled: question?.enabled,
    commentCount: question?.comments?.length || 0,
    comments: question?.comments || [],
    percentage: total ? Number(((correct / total) * 100).toFixed(2)) : 0,
  };
  return { choiceSummary, metricsSummary };
};
